import { Observable } from "rxjs";

export interface OmsButtonContent {
	text?: string;
	url?: string;
	onClick?: string;
	target?: string;
	cssClass?: string;
}

export interface OmsImageContent {
	url: string;
	altText?: string;
	height?: number;
	width?: number;
}

export const enum OmsMessageAction {
	Read = "Read",
	Clicked = "Clicked",
	PopupShown = "PopupShown",
	Hide = "Hide",
	Unhide = "Unhide",
}

export const enum OmsRoute {
	Messages = "messages",
	Active = "messages.active",
	Expired = "messages.expired",
}

export const enum OmsMessageStatus {
	Active = "active",
	Expired = "expired",
}

export const NOTIFICATION_STATUS_DICTIONARY = {
	[OmsMessageAction.Read]: { isRead: true },
	[OmsMessageAction.Hide]: { isHidden: true },
	[OmsMessageAction.Clicked]: { isClicked: true },
	[OmsMessageAction.Unhide]: { isHidden: false },
	[OmsMessageAction.PopupShown]: undefined,
};

type Dictionary<T> = { [Key in keyof T]: T[Key] };

export type NotificationStatusDictionary = Dictionary<typeof NOTIFICATION_STATUS_DICTIONARY>;

export interface NotificationsState {
	notificationsByStatus: Record<OmsMessageStatus, OmsMessage[]>;
	notificationsInfo: NotificationsInfo;
}
export interface OmsState extends NotificationsState {
	isBusy: boolean;
	alerts: OmsMessage[];
	userSettings: OmsUserSettings;
	isAlertRealtimeEnabled: boolean;
	isNotificationInfoRealtimeEnabled: boolean;
	alertContext: string;
}

export interface NotificationsInfoRequest {
	popup: boolean;
	expired: boolean;
	hidden: boolean;
}

export interface NotificationsInfo {
	unreadNotificationCount: number;
	popupNotification?: OmsMessage;
	unreadProactiveChatCount: number;
}

export interface OmsMessage {
	id?: number;
	isRead?: boolean;
	isClicked?: boolean;
	isHidden?: boolean;
	sendDate?: string;
	expirationDate?: string;
	content?: OmsMessageContent;
	selected?: boolean;
}

export interface OmsMessageContent {
	title: string;
	body: string;
	imageName: string;
	subHeadline?: string;
	significantTerms?: string;
	termsAndConditions?: string;
	cta: OmsButtonContent;
	image: OmsImageContent;
}

export interface UpdateMessage {
	messageId: number;
	action: OmsMessageAction;
}

export interface PayloadStatus {
	status: OmsMessageStatus;
}

export interface UpdateMessagePayload extends PayloadStatus {
	message: UpdateMessage;
}

export interface UpdateMessagesPayload extends PayloadStatus {
	messages: UpdateMessage[];
}

export interface OmsUserSettings {
	allowPopupNotifications?: boolean;
	showExpiredMessages?: boolean;
	showHiddenMessages?: boolean;
}

export abstract class OmsService {
	abstract getNotificationsByStatus(_state: OmsMessageStatus): Observable<OmsMessage[]>;

	abstract updateMessage(_updateMessage: UpdateMessage): Observable<unknown>;

	abstract updateMessages(_updateMessages: UpdateMessage[]): Observable<unknown>;

	abstract getAlerts(_context: string): Observable<OmsMessage[]>;

	abstract getNotificationsInfo(_notificationInfoParams: NotificationsInfoRequest): Observable<NotificationsInfo>;

	abstract getUserSettings(): Observable<OmsUserSettings>;

	abstract updateUserSettings(_userSettings: OmsUserSettings): Observable<OmsUserSettings>;
}
